import { useEffect, useRef, useState } from 'react';

import { Button } from '@oui/app-core/src/components/Button';
import { ErrorPresenter } from '@oui/app-core/src/components/ErrorPresenter';
import { Icon } from '@oui/app-core/src/components/Icon';
import MediaPlayer from '@oui/app-core/src/components/MediaPlayer';
import { Heading, Label, Text } from '@oui/app-core/src/components/Text';
import { View } from '@oui/app-core/src/components/View';

import { StressBreathingExerciseType } from '../../types/navigation';
import { useStressBreathingContext } from '../StressBreathingContext';

const HOLDING_VIDEO = 'https://stream.mux.com/SpfsLhy02zGvFPNUGweHS0100aNKppH7xPtNkAnOwZzbhQ.m3u8';
const HYPERVENTILATE_VIDEO =
  'https://stream.mux.com/r9NIMfBhpHlYoezMwRP00XFbI6v8usOagnxCPAtX00jUc.m3u8';

/**
 * UI to render while a round of the exercise is being performed.
 */
export const StressBreathingRound = ({
  type,
  round,
  onFinish,
  pause,
}: {
  onFinish: () => unknown;
  type: StressBreathingExerciseType;
  round: number;
  pause?: boolean;
  /**
   * Allows changing the duration of the round. Useful for testing purposes
   */
}) => {
  const hasFinished = useRef(false);
  const videoRef = useRef<MediaPlayer>(null);
  const { durationMs } = useStressBreathingContext();
  const [msRemaining, setMsRemaining] = useState(durationMs);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState(false);

  const secondsRemaining = Math.floor(msRemaining / 1000);
  const minutesRemaining = Math.floor(secondsRemaining / 60);
  const timer = `${minutesRemaining}:${(secondsRemaining % 60).toString().padStart(2, '0')}`;

  if (secondsRemaining <= 0 && !hasFinished.current) {
    hasFinished.current = true;
    onFinish();
  }

  useEffect(() => {
    if (pause) setIsPlaying(false);
  }, [pause]);

  useEffect(() => {
    let start = Date.now();
    let timeout: NodeJS.Timeout;

    if (isPlaying) {
      timeout = setInterval(() => {
        const now = Date.now();
        setMsRemaining((ms) => ms - (now - start));
        start = now;
      }, 100);
    }

    return () => clearTimeout(timeout);
  }, [isPlaying]);

  const description =
    type === 'holding'
      ? 'Hold your breath for 60 seconds.'
      : 'Breathe fast and deep for 60 seconds.';

  useEffect(() => {
    if (videoRef.current) {
      if (videoRef.current.state.isPlaying !== isPlaying) {
        void videoRef.current.setIsPlayingAsync(isPlaying);
      }
    }
  }, [isPlaying]);

  return (
    <View testID="StressBreathingRound" style={{ gap: 50 }}>
      <Heading level={2} text={`Round ${round + 1}`} textAlign="center" color="white" />
      <Label textAlign="center" text={description} color="white" />
      {error ? <ErrorPresenter errorString="Something went wrong. Please try again later" /> : null}
      <View style={{ borderRadius: 30, backgroundColor: 'white', overflow: 'hidden' }}>
        {isCollapsed ? (
          <Button
            variant="text"
            text="Follow along video"
            onPress={() => setIsCollapsed(false)}
            alignSelf="center"
          />
        ) : (
          <View
            // media player pauses at a slightly different time so the snapshot
            // diff detects a few pixels that we don't care about
            // @ts-expect-error
            dataSet={{ chromatic: 'ignore' }}
          >
            <MediaPlayer
              uri={type === 'holding' ? HOLDING_VIDEO : HYPERVENTILATE_VIDEO}
              resizeMode="contain"
              hideControls
              ref={videoRef}
              autoPlay={isPlaying}
              initialPositionMillis={durationMs - msRemaining}
              onLoad={() => setIsLoaded(true)}
              onError={() => {
                setError(true);
              }}
            />
            <Icon
              name="collapse"
              color="white"
              style={{ position: 'absolute', bottom: 8, right: 8 }}
              aria-label="Hide video"
              onPress={() => setIsCollapsed(true)}
            />
          </View>
        )}
        <View style={{ padding: 20, paddingBottom: 30 }}>
          <Text text={timer} size={64} weight="bold" textAlign="center" />
        </View>
      </View>
      {!isLoaded ? null : isPlaying ? (
        <Button
          text={undefined}
          aria-label="Pause"
          icon="pause"
          onPress={() => setIsPlaying(false)}
          size="large"
          alignSelf="center"
        />
      ) : (
        <Button
          text={undefined}
          aria-label="Play"
          icon="play"
          onPress={() => setIsPlaying(true)}
          size="large"
          alignSelf="center"
        />
      )}
    </View>
  );
};
